import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome } from "@fortawesome/pro-regular-svg-icons/faHome";
import { faGraduationCap } from "@fortawesome/pro-regular-svg-icons/faGraduationCap";
import { faSparkles } from "@fortawesome/pro-regular-svg-icons/faSparkles";
import { faHeadset } from "@fortawesome/pro-regular-svg-icons/faHeadset";
import { faHome as faHomeSolid } from "@fortawesome/pro-solid-svg-icons/faHome";
import { faGraduationCap as faGraduationCapSolid } from "@fortawesome/pro-solid-svg-icons/faGraduationCap";
import { faSparkles as faSparklesSolid } from "@fortawesome/pro-solid-svg-icons/faSparkles";
import { faHeadset as faHeadsetSolid } from "@fortawesome/pro-solid-svg-icons/faHeadset";
import { useState } from "react";
import { getUrls } from "@portal/app/_common/lib/urls";
import { usePathname, useRouter } from "next/navigation";
import { capitalCase } from "@portal/app/_common/lib/changeCase";
import { styled } from "@mui/material/styles";
import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import { InProgressBadge } from "@portal/app/dash/_components/InProgressBadge";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import { getDashboardDockDestination } from "./reminderUtils";
import { useCurrentDashboardResumables } from "./useCurrentDashboardResumables";

const ROUTES = {
  DASHBOARD: "DASHBOARD",
  CLASSES: "CLASSES",
  RESOURCES: "RESOURCES",
  SUPPORT: "SUPPORT",
  // JOBS BOARD: Soft-sunset-- keeping code around for now
  // JOBS: "JOBS",
} as const;

export const useHasDashFooter = () => {
  const { loggedIn } = useAuthenticatedContext();
  return loggedIn;
};

export const DashFooter = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { customerId, loggedIn, role } = useAuthenticatedContext();
  const { clientLocation } = usePageContext();
  const { enrollments } = useCurrentDashboardResumables({
    skip: !loggedIn || !role.isCustomer || !customerId,
  });
  const resumableCount = enrollments?.length ?? 0;
  const urls = getUrls("CUSTOMER");
  const [currentRoute, setCurrentRoute] = useState(() => {
    if (urls.Dashboard().endsWith(pathname)) {
      return ROUTES.DASHBOARD;
    } else if (urls.DashboardResources().endsWith(pathname)) {
      return ROUTES.RESOURCES;
    } else if (urls.DashboardSupport().endsWith(pathname)) {
      return ROUTES.SUPPORT;
      // JOBS BOARD: Soft-sunset-- keeping code around for now
      // } else if (urls.DashboardJobs().endsWith(pathname)) {
      //   return ROUTES.JOBS;
    } else if (urls.DreamSearchMatches(pathname)) {
      return ROUTES.CLASSES;
    } else {
      return null;
    }
  });

  if (!loggedIn) {
    return null;
  }

  return (
    <FloatingBottomNav
      showLabels
      value={currentRoute}
      onChange={(_, newValue) => {
        setCurrentRoute(newValue);
      }}
    >
      <BottomNavigationAction
        data-testid="dash-footer-dashboard"
        label={capitalCase(ROUTES.DASHBOARD)}
        value={ROUTES.DASHBOARD}
        onClick={() => router.push(getDashboardDockDestination(urls.Dashboard(), resumableCount))}
        icon={
          <InProgressBadge count={resumableCount} position={{ top: "5%", right: "-10%" }}>
            <FontAwesomeIcon
              icon={currentRoute === ROUTES.DASHBOARD ? faHomeSolid : faHome}
              size="xl"
            />
          </InProgressBadge>
        }
      />
      {/* JOBS BOARD: Soft-sunset-- keeping code around for now */}
      {/* <BottomNavigationAction
        data-testid="dash-footer-jobs"
        label={capitalCase(ROUTES.JOBS)}
        value={ROUTES.JOBS}
        onClick={() => router.push(urls.DashboardJobs())}
        icon={
          <FontAwesomeIcon
            icon={currentRoute === ROUTES.JOBS ? faBriefcaseSolid : faBriefcase}
            size="xl"
          />
        }
      /> */}
      <BottomNavigationAction
        data-testid="dash-footer-classes"
        label={capitalCase(ROUTES.CLASSES)}
        value={ROUTES.CLASSES}
        onClick={() => router.push(urls.DreamSearch({ zip: clientLocation.zip }))}
        icon={
          <FontAwesomeIcon
            icon={currentRoute === ROUTES.CLASSES ? faGraduationCapSolid : faGraduationCap}
            size="xl"
          />
        }
      />
      <BottomNavigationAction
        data-testid="dash-footer-resources"
        label={capitalCase(ROUTES.RESOURCES)}
        value={ROUTES.RESOURCES}
        onClick={() => router.push(urls.DashboardResources())}
        icon={
          <FontAwesomeIcon
            icon={currentRoute === ROUTES.RESOURCES ? faSparklesSolid : faSparkles}
            size="xl"
          />
        }
      />
      <BottomNavigationAction
        data-testid="dash-footer-support"
        label={capitalCase(ROUTES.SUPPORT)}
        value={ROUTES.SUPPORT}
        onClick={() => router.push(urls.DashboardSupport())}
        icon={
          <FontAwesomeIcon
            icon={currentRoute === ROUTES.SUPPORT ? faHeadsetSolid : faHeadset}
            size="xl"
          />
        }
      />
    </FloatingBottomNav>
  );
};

const FloatingBottomNav = styled(BottomNavigation)`
  position: fixed;
  width: 100%;
  bottom: 0;
  left: 0;
  z-index: 100;

  ${({ theme }) => theme.breakpoints.up("md")} {
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    min-width: 30rem;
    border-radius: 1rem;
    box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.12);
    border: 1px solid ${({ theme }) => theme.palette.divider};
    background-color: ${({ theme }) => theme.palette.background.paper};
    padding: 0.75rem 1.5rem;
    gap: 2.5rem;

    & .MuiBottomNavigationAction-root {
      flex-direction: row;
      gap: 0.5rem;
      min-width: auto;
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      transition: background-color 0.2s ease;

      & .MuiBottomNavigationAction-label {
        font-size: 0.875rem;
        font-weight: 500;
        opacity: 1;
        transform: none;
        margin-top: 0;
      }

      &:hover {
        background-color: ${({ theme }) => theme.palette.action.hover};
      }

      &.Mui-selected {
        & .MuiBottomNavigationAction-label {
          color: ${({ theme }) => theme.palette.primary.main};
        }
      }
    }
  }
`;
